import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { notifyApprovalResolved } from '@/lib/notifications';
import { trackApprovalSubmitted, trackApprovalResolved } from '@/lib/analytics';

export interface ApprovalRequest {
  id: string;
  type: 'expense' | 'time_off' | 'purchase' | 'document' | 'other';
  title: string;
  description: string | null;
  amount: number | null;
  currency: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requester_id: string | null;
  approver_id: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  organization_id: string | null;
}

export interface CreateApprovalData {
  type: 'expense' | 'time_off' | 'purchase' | 'document' | 'other';
  title: string;
  description?: string;
  amount?: number;
  currency?: string;
}

export function useApprovals() {
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { session } = useAuth();
  const { organization } = useOrganization();
  const { toast } = useToast();

  const logActivity = async (action: string, entityId: string, details: Record<string, unknown>) => {
    try {
      await supabase.rpc('log_activity', {
        p_action: action,
        p_entity_type: 'approval',
        p_entity_id: entityId,
        p_details: JSON.parse(JSON.stringify(details))
      });
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };

  const fetchApprovals = async () => {
    try {
      // Use secure RPC function that masks financial data based on user role
      const { data, error } = await supabase
        .rpc('get_approval_requests_safe');

      if (error) throw error;
      // Sort by created_at descending since RPC doesn't support ORDER BY
      const sortedData = (data || []).sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setApprovals(sortedData as ApprovalRequest[]);
    } catch (error) {
      console.error('Error fetching approvals:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch approval requests.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const createApproval = async (data: CreateApprovalData) => {
    try {
      const { data: newApproval, error } = await supabase.from('approval_requests').insert({
        ...data,
        requester_id: session?.user?.id,
        organization_id: organization?.id || null
      }).select().single();

      if (error) throw error;
      
      // Log activity
      if (newApproval) {
        await logActivity('approval_created', newApproval.id, { title: data.title, type: data.type });
        trackApprovalSubmitted(data.type);
      }
      
      toast({ title: 'Success', description: 'Approval request submitted.' });
      await fetchApprovals();
      return true;
    } catch (error) {
      console.error('Error creating approval:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit approval request.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const updateApprovalStatus = async (
    id: string, 
    status: 'approved' | 'rejected', 
    rejectionReason?: string
  ) => {
    try {
      const approval = approvals.find(a => a.id === id);
      
      const updates: Partial<ApprovalRequest> = {
        status,
        approved_by: session?.user?.id,
        approved_at: new Date().toISOString()
      };

      if (status === 'rejected' && rejectionReason) {
        updates.rejection_reason = rejectionReason;
      }

      const { error } = await supabase
        .from('approval_requests')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      // Log activity
      await logActivity(`approval_${status}`, id, { title: approval?.title, status });
      trackApprovalResolved(status);

      // Send notification to requester using secure profile function
      if (approval?.requester_id) {
        const { data: requesterProfiles } = await supabase
          .rpc('get_profile_safe', { p_user_id: approval.requester_id });
        const requesterProfile = requesterProfiles?.[0];

        const { data: approverProfiles } = await supabase
          .rpc('get_profile_safe', { p_user_id: session?.user?.id || '' });
        const approverProfile = approverProfiles?.[0];

        if (requesterProfile) {
          notifyApprovalResolved(
            requesterProfile.email,
            requesterProfile.full_name,
            approval.title,
            status,
            approverProfile?.full_name || null
          ).catch(console.error);
        }
      }
      
      toast({ 
        title: 'Success', 
        description: `Request ${status === 'approved' ? 'approved' : 'rejected'}.` 
      });
      await fetchApprovals();
      return true;
    } catch (error) {
      console.error('Error updating approval:', error);
      toast({
        title: 'Error',
        description: 'Failed to update approval status.',
        variant: 'destructive'
      });
      return false;
    }
  };

  useEffect(() => {
    if (session) {
      fetchApprovals();

      const channel = supabase
        .channel('approvals-changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'approval_requests' },
          () => {
            fetchApprovals();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [session]);

  return {
    approvals,
    loading,
    fetchApprovals,
    createApproval,
    updateApprovalStatus
  };
}
